import { Link } from 'react-router-dom';
import { products } from '../../../mocks/products';
import { useCart } from '../../../context/CartContext';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const FeaturedProducts = () => {
  const { addToCart } = useCart();
  const { t } = useTranslation();
  const [added, setAdded] = useState<string | null>(null);
  const featured = products.slice(0, 4);

  const handleAdd = (product: typeof products[0]) => {
    addToCart(product);
    setAdded(product.id);
    setTimeout(() => setAdded(null), 1500);
  };

  return (
    <section className="py-20 bg-gradient-to-b from-[#0a0018] to-[#050010]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-violet-400 text-xs font-semibold uppercase tracking-widest mb-3">{t('home_bestsellers_badge')}</p>
          <h2 className="text-3xl lg:text-4xl font-bold text-white">{t('home_bestsellers_title')}</h2>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop>
          {featured.map(product => (
            <div
              key={product.id}
              className="group bg-[#110020]/80 border border-violet-500/15 rounded-2xl overflow-hidden hover:border-violet-500/40 transition-all duration-300"
            >
              {/* Image */}
              <div className="relative h-52 overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                {product.badge && (
                  <span className="absolute top-3 left-3 px-2.5 py-1 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-xs font-bold rounded-full">
                    {product.badge}
                  </span>
                )}
                {product.originalPrice && (
                  <span className="absolute top-3 right-3 px-2 py-1 bg-pink-600/80 text-white text-xs font-bold rounded-lg">
                    -{Math.round((1 - product.price / product.originalPrice) * 100)}%
                  </span>
                )}
              </div>

              {/* Info */}
              <div className="p-4">
                <p className="text-violet-400/60 text-xs uppercase tracking-wide mb-1">{product.category}</p>
                <h3 className="text-white font-semibold text-sm mb-3 leading-snug">{product.name}</h3>

                {/* Rating */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex items-center gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className={`ri-star-fill text-xs ${i < Math.floor(product.rating) ? 'text-[#d4a017]' : 'text-white/15'}`} />
                    ))}
                  </div>
                  <span className="text-white/35 text-xs">({product.reviews})</span>
                </div>

                {/* Price & Add */}
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-white font-bold text-lg">&euro;{product.price.toFixed(2)}</span>
                    {product.originalPrice && (
                      <span className="text-white/25 text-xs line-through ml-1.5">&euro;{product.originalPrice.toFixed(2)}</span>
                    )}
                  </div>
                  <button
                    onClick={() => handleAdd(product)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-300 cursor-pointer ${
                      added === product.id
                        ? 'bg-violet-600 text-white'
                        : 'bg-violet-600/15 text-violet-300 hover:bg-violet-600/30 border border-violet-500/20'
                    }`}
                  >
                    {added === product.id ? (
                      <><i className="ri-check-line" /> {t('cta_added')}</>
                    ) : (
                      <><i className="ri-shopping-bag-line" /> {t('cta_add')}</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-10">
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 px-8 py-3.5 border border-violet-500/30 text-violet-300 rounded-full text-sm whitespace-nowrap hover:bg-violet-500/10 transition-colors cursor-pointer"
          >
            {t('cta_viewAllProducts')}
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
};
